import type { AxiosInstance } from "axios";
import type { TranslateFn, U2000ApiErrorResult } from "../u2000Core/axios/U2000HandleApiError";
import type { U2000SearchPayload } from "../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GenerateUrlUUID } from "../u2000Core/tools/U2000UrlHelpers";
import type { U3129PeriodPreset } from "./tools/U3129PeriodPresets";
import { u3129ClientDisplayFromHonorariumRow, u3129ProjectDisplayLabel } from "./tools/u3129BuildHonorariumLinesFromTasks";
import type { TsTaxRateComponentApplicationMode } from "../u3110TaxRate/U3110Service";

export type { U3129PeriodPreset };

const LOGICALUNIT_URL = "U3129";

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  FINDPROJECT: string;
  FINDSUBPROJECT: string;
  FINDEMPLOYEE: string;
  FINDAVAILABLEPERIODS: string;
  FINDTASKSFORPROJECT: string;
  FINDTASKSNOTASSIGNEDTOPROJECT: string;
  ASSIGNTASKSTOPROJECTSCOPE: string;
  FINDCOMMONTASKSFORPROJECTS: string;
  FINDACTIVETASKS: string;
  ASSIGNTASKSTOSELECTEDPROJECTS: string;
  /** Valeurs par défaut pour une nouvelle feuille (POST corps JSON : employeeId, periodDate). */
  INIT_FOR_CREATE: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  SUBMIT_FOR_APPROVAL: (id: string | number) => string;
  REOPEN: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  FINDPROJECT: `${LOGICALUNIT_URL}/findProject`,
  FINDSUBPROJECT: `${LOGICALUNIT_URL}/findSubproject`,
  FINDEMPLOYEE: `${LOGICALUNIT_URL}/findEmployee`,
  FINDAVAILABLEPERIODS: `${LOGICALUNIT_URL}/findAvailablePeriods`,
  FINDTASKSFORPROJECT: `${LOGICALUNIT_URL}/findTasksForProject`,
  FINDTASKSNOTASSIGNEDTOPROJECT: `${LOGICALUNIT_URL}/findTasksNotAssignedToProject`,
  ASSIGNTASKSTOPROJECTSCOPE: `${LOGICALUNIT_URL}/assignTasksToProjectScope`,
  FINDCOMMONTASKSFORPROJECTS: `${LOGICALUNIT_URL}/findCommonTasksForProjects`,
  FINDACTIVETASKS: `${LOGICALUNIT_URL}/findActiveTasks`,
  ASSIGNTASKSTOSELECTEDPROJECTS: `${LOGICALUNIT_URL}/assignTasksToSelectedProjects`,
  INIT_FOR_CREATE: `${LOGICALUNIT_URL}/initForCreate`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  SUBMIT_FOR_APPROVAL: (id: string | number) => `${LOGICALUNIT_URL}/submitForApproval/${id}`,
  REOPEN: (id: string | number) => `${LOGICALUNIT_URL}/reopen/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
};

/** Statut feuille de temps (aligné backend TsTimesheetStatus). */
export type U3129TimesheetStatus = "Open" | "WaitingForApproval" | "Approve" | "WaitingForGenerateTEC" | "Close";

/** Statuts après approbation — affichage en-tête « heures paye / banque » (parité U3130). */
export const U3129_PROCESSED_TIMESHEET_STATUSES = ["Approve", "WaitingForGenerateTEC", "Close"] as const;

/** Rémunération employé dans le bloc `employee` du GET feuille (aligné backend). */
export type U3129RemunerationType = "Hourly" | "Salary";

/** Référentiel type équipement (initLU) — aligné GET /U3129/initLU (schéma backend U3129EquipmentTypeItem). */
export interface U3129EquipmentTypeItem {
  id: string;
  code?: string;
  description?: string;
  billingUnit?: string;
  amount: number;
  active: number;
  majorFactor: number;
}

/** Ligne composante dans `taxRates[]` de initLU (directive U3110 §13, schéma U3129). */
export interface U3129TaxRateComponentRef {
  sortOrder: number;
  rate: number;
  labelFr?: string | null;
  labelEn?: string | null;
  applicationMode: TsTaxRateComponentApplicationMode | null;
}

/** Référentiel taux de taxe (initLU). */
export interface U3129TaxRateItem {
  id: string;
  code?: string;
  /** Taux agrégé (%) ; peut être absent selon la payload initLU — utiliser {@link u3129TaxRateDisplayPercent}. */
  rate?: number;
  description?: string;
  /** Si `false`, régime inactif — exclure des combos (défaut true si absent). */
  active?: boolean;
  countryId?: string | null;
  provinceId?: string | null;
  /** Optionnel : détail des composantes pour calculs / affichage avancé. */
  taxRateComponents?: U3129TaxRateComponentRef[];
}

/** Taux sélectionnable en feuille de temps : actif uniquement (pas de filtre géographique). */
export function u3129IsTaxRateActive(item: U3129TaxRateItem): boolean {
  return item.active !== false;
}

/** Pourcentage affichable pour un taux (champ `rate` ou somme des composantes). */
export function u3129TaxRateDisplayPercent(item: U3129TaxRateItem): number | null {
  if (typeof item.rate === "number" && Number.isFinite(item.rate)) {
    return item.rate;
  }
  const comps = item.taxRateComponents;
  if (comps?.length) {
    let sum = 0;
    let hasAny = false;
    for (const c of comps) {
      const r = c.rate;
      if (typeof r === "number" && Number.isFinite(r)) {
        sum += r;
        hasAny = true;
      }
    }
    if (hasAny) {
      return sum;
    }
  }
  return null;
}

/** Libellé combo « Taux taxe » : évite « undefined% » si `rate` est absent ; option vide = description seule. */
export function u3129FormatTaxRateComboLabel(item: U3129TaxRateItem): string {
  if (!item.id?.trim()) {
    return item.description?.trim() ?? "";
  }
  const pct = u3129TaxRateDisplayPercent(item);
  const code = item.code?.trim();
  const ratePart = pct != null ? `${pct}%` : "";

  if (code) {
    return ratePart ? `${code} (${ratePart})` : code;
  }
  if (ratePart) {
    return ratePart;
  }
  return item.description?.trim() ?? "";
}

/** Référentiel type dépense (initLU) — GET /U3129/initLU result.expenseTypes[]. */
export interface U3129ExpenseTypeItem {
  id: string;
  code?: string | null;
  description?: string | null;
  /** Souvent utilisé comme coût unitaire côté API si `unitCost` est absent. */
  factor?: number | null;
  /** Défaut métier 0 après migration. */
  majorFactor?: number;
  /** Petit entier (ex. actif/inactif) ; défaut métier 1. */
  status?: number;
  /** Coût unitaire (initLU / API), alimente la ligne dépense à la sélection du type. */
  unitCost?: number | null;
}

/** Tâche (initLU). */
export interface U3129TaskItem {
  id: string;
  taskCode?: string;
  /** Libellé tâche (initLU), selon langue UI. */
  taskDescriptionFr?: string;
  taskDescriptionEn?: string;
  /** Natures punchables ∩ projet (findTasks*, lecture seule — BL-0677). */
  workNatureDisplay?: string;
  /** Descriptions natures (intersection), jointes par « · » — affichage ligne tâche. */
  workNatureDescriptionFr?: string;
  workNatureDescriptionEn?: string;
}

/** Personnalisations retournées par initLU (comme U3121). */
export interface U3129InitLUPersonalization {
  admIncludeSubProject: string | null;
  /** BL-0606 : true = pas de garde-fou UI soldes banques à la soumission (legacy). */
  skipHourBankBalanceValidationOnSubmit?: boolean;
  /** BL-0675 : true = masquer onglets Dépenses et Équipement. */
  hideExpenseAndEquipmentTabs?: boolean;
}

/** Employé associé à l'usager courant (initLU). Null si l'usager n'est pas configuré pour faire sa feuille de temps. */
export interface U3129InitLUEmployee {
  id: string;
  firstName: string;
  lastName: string;
  vacationBncHours?: number;
  vacationNegativeThresholdHours?: number;
  holidayBncHours?: number;
  sickLeaveBncHours?: number;
  specialBncHours?: number;
}

export interface U3129InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  /** Préréglage filtre période (liste) ; affiché en puces dans BasicSearch. Défaut frontend : semaine courante. */
  defaultPeriodPreset?: U3129PeriodPreset;
  equipmentTypes: U3129EquipmentTypeItem[];
  taxRates: U3129TaxRateItem[];
  expenseTypes: U3129ExpenseTypeItem[];
  /** Optionnel : les tâches honoraires par ligne viennent de `findTasksForProject` ; ne plus alimenter pour alléger initLU. */
  tasks?: U3129TaskItem[];
  /** Personnalisations d'organisation (ex. ADM_INCLUDE_SUB_PROJET). */
  personalization?: U3129InitLUPersonalization;
  /** Employé lié à l'usager. Null = usager non configuré, afficher la page « Contacter votre administrateur ». */
  employee?: U3129InitLUEmployee | null;
  /** Fuseau d'affichage organisation (IANA). Défaut frontend : America/Montreal. */
  organizationTimezone?: string;
}

/** Convention UI : type de dépense sélectionnable si status === 1 (défaut métier après migration). */
export function u3129IsExpenseTypeActive(item: U3129ExpenseTypeItem): boolean {
  return (item.status ?? 1) === 1;
}

/** majorFactor normalisé (initLU / affichage). */
export function u3129ExpenseTypeMajorFactor(item: U3129ExpenseTypeItem): number {
  return item.majorFactor ?? 0;
}

/** Libellé type dépense (description, code ou id) — sans coût ni coefficient (colonne « Coût unitaire »). */
export function u3129ExpenseTypeLabel(item: U3129ExpenseTypeItem): string {
  return (item.description?.trim() || item.code?.trim() || item.id) ?? item.id;
}

/** Alias historique : même rendu que {@link u3129ExpenseTypeLabel} (plus de valeur entre parenthèses dans le combo). */
export function u3129ExpenseTypeLabelWithFactor(item: U3129ExpenseTypeItem): string {
  return u3129ExpenseTypeLabel(item);
}

/**
 * Coût unitaire depuis le référentiel type dépense : `unitCost` si défini, sinon repli sur `factor` (API existante).
 */
export function u3129ExpenseTypeUnitCost(item: U3129ExpenseTypeItem): number {
  const u = item.unitCost;
  if (typeof u === "number" && Number.isFinite(u)) return u;
  const f = item.factor;
  if (typeof f === "number" && Number.isFinite(f)) return f;
  return 0;
}

/** Ligne avec coût unitaire > 0 : montant dérivé (coût × quantité entière). */
export function u3129ExpenseLineUsesUnitCost(row: Pick<U3129ExpenseItem, "unitCost">): boolean {
  const u = row.unitCost;
  return typeof u === "number" && Number.isFinite(u) && u > 0;
}

/** Montant = coût unitaire × quantité entière (arrondi 2 décimales). */
export function u3129ComputeExpenseLineAmountFromUnit(row: Pick<U3129ExpenseItem, "unitCost" | "quantity">): number {
  const c = typeof row.unitCost === "number" && Number.isFinite(row.unitCost) ? row.unitCost : 0;
  const q = row.quantity;
  const qi = typeof q === "number" && Number.isFinite(q) && Number.isInteger(q) ? q : 0;
  return Math.round(c * qi * 100) / 100;
}

/** Type équipement sélectionnable si actif (active === 1, défaut métier). */
export function u3129IsEquipmentTypeActive(item: U3129EquipmentTypeItem): boolean {
  return (item.active ?? 1) === 1;
}

/** Montant unitaire normalisé (initLU). */
export function u3129EquipmentTypeAmount(item: U3129EquipmentTypeItem): number {
  return typeof item.amount === "number" && Number.isFinite(item.amount) ? item.amount : 0;
}

/** Facteur de majoration normalisé (initLU). */
export function u3129EquipmentTypeMajorFactor(item: U3129EquipmentTypeItem): number {
  return item.majorFactor ?? 0;
}

/** Projet (findProject). */
export interface U3129FindProjectItem {
  id: string;
  projetNumber?: string;
  projetName?: string;
  /** Si true, le champ sous-projet est disponible pour cette ligne. */
  includeSubProjet?: boolean;
  customerCode?: string;
  customerName?: string;
  /** Natures assignées au projet, format MyEvent (lecture seule — BL-0677). */
  workNatureDisplay?: string;
}

/** Sous-projet (findSubproject). */
export interface U3129FindSubprojectItem {
  id: string;
  projectId: string;
  subProjetNumber?: string;
  subProjetName?: string;
}

/** Employé (autocomplete findEmployee). */
export interface U3129FindEmployeeItem {
  id: string;
  codeEmployee?: string;
  firstName?: string;
  lastName?: string;
}

/** Projet (objet retourné par le GET sur une ligne honoraire). */
export interface U3129HonorariumProjectRef {
  id: string;
  projetNumber?: string;
  /** Nom / description U3121 (lecture GET — BL-0679). */
  projetName?: string;
  nomClient?: string;
  /** Libellé bloc client calculé backend (BL-0679). */
  clientBlockLabel?: string;
  workNatureDisplay?: string;
}

/** Tâche (objet retourné par le GET sur une ligne honoraire). */
export interface U3129HonorariumTaskRef {
  id: string;
  description?: string;
  workNatureDisplay?: string;
  workNatureDescriptionFr?: string;
  workNatureDescriptionEn?: string;
}

/** Identifiant stable côté UI pour associer cache de tâches (projet/sous-projet) à une ligne honoraire. */
export function newHonorariumClientRowId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `hr-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Ligne honoraire. */
export interface U3129HonorariumItem {
  id?: string;
  timesheetId?: string;
  /** Clé client pour le cache des listes de tâches par ligne (non envoyée au backend). */
  clientRowId?: string;
  projectId: string;
  subprojectId?: string;
  monday?: number;
  tuesday?: number;
  wednesday?: number;
  thursday?: number;
  friday?: number;
  saturday?: number;
  sunday?: number;
  taskType?: string;
  version?: number;
  /** Objet projet (réponse GET). */
  project?: U3129HonorariumProjectRef;
  /** Objet sous-projet (réponse GET). */
  subproject?: U3129LineSubprojectRef | U3129FindSubprojectItem;
  /** Objet tâche (réponse GET). */
  task?: U3129HonorariumTaskRef;
  totalHours?: number;
}

/** Sous-projet (objet retourné par le GET sur une ligne dépense / équipement). */
export interface U3129LineSubprojectRef {
  id: string;
  subProjetNumber?: string;
  subProjetName?: string;
}

/** Ligne dépense. */
export interface U3129ExpenseItem {
  id?: string;
  timesheetId?: string;
  transactionDate?: string | null;
  projectId: string;
  subprojectId?: string;
  expenseTypeId: string;
  /** Coût unitaire (souvent issu du type de dépense ; lecture seule en UI). `null` = rien à afficher. */
  unitCost?: number | null;
  taxRateId?: string;
  quantity?: number | null;
  amount: number;
  remark?: string;
  version?: number;
  /** Clé stable pour les listes éditables (non envoyée au backend). */
  clientRowId?: string;
  /** Objet projet (réponse GET). */
  project?: U3129HonorariumProjectRef | U3129FindProjectItem;
  /** Objet sous-projet (réponse GET). */
  subproject?: U3129LineSubprojectRef | U3129FindSubprojectItem;
  /** Affichage client (UI, non API). */
  clientDisplay?: string;
  checked?: boolean;
}

/** Ligne équipement. */
export interface U3129EquipmentItem {
  id?: string;
  timesheetId?: string;
  transactionDate?: string | null;
  projectId: string;
  subprojectId?: string;
  equipmentTypeId: string;
  billingUnit?: string;
  equipmentCost?: number;
  quantity?: number;
  amount?: number;
  remark?: string;
  version?: number;
  clientRowId?: string;
  project?: U3129HonorariumProjectRef | U3129FindProjectItem;
  subproject?: U3129LineSubprojectRef | U3129FindSubprojectItem;
  clientDisplay?: string;
  checked?: boolean;
}

/** Ligne banque d'heures. */
export interface U3129BankHoursItem {
  id?: string;
  timesheetId?: string;
  bankId?: string;
  monday?: number;
  tuesday?: number;
  wednesday?: number;
  thursday?: number;
  friday?: number;
  saturday?: number;
  sunday?: number;
  version?: number;
  /** Total heures sur la ligne (calcul UI, non envoyé au backend). */
  totalHours?: number;
}

/**
 * Ordre grille : congés puis retrait (`special`) et dépôt (`specialDeposit`) banque accumulée.
 */
export const U3129_BANK_HOUR_TYPE_ORDER = [
  "vacation",
  "holidayLeave",
  "sickLeave",
  "authorizedLeave",
  "special",
  "specialDeposit",
] as const;

export type U3129BankHourTypeId = (typeof U3129_BANK_HOUR_TYPE_ORDER)[number];

export function emptyBankHoursRow(bankId: U3129BankHourTypeId): U3129BankHoursItem {
  return {
    bankId,
    monday: 0,
    tuesday: 0,
    wednesday: 0,
    thursday: 0,
    friday: 0,
    saturday: 0,
    sunday: 0,
  };
}

function normalizeBankHoursBankId(raw: string | undefined): string | null {
  const id = raw?.trim();
  return id || null;
}

/** Fusionne avec le référentiel des types pour toujours avoir une ligne par `bankId`, dans l’ordre métier. */
export function ensureBankHoursList(list: U3129BankHoursItem[] | undefined | null): U3129BankHoursItem[] {
  const byId = new Map<string, U3129BankHoursItem>();
  for (const item of list ?? []) {
    const id = normalizeBankHoursBankId(item.bankId);
    if (id) {
      byId.set(id, { ...item, bankId: id });
    }
  }
  return U3129_BANK_HOUR_TYPE_ORDER.map((bankId) => {
    const existing = byId.get(bankId);
    if (existing) {
      return { ...emptyBankHoursRow(bankId), ...existing, bankId };
    }
    return emptyBankHoursRow(bankId);
  });
}

/** Employé (résumé dans timesheet) — GET `/:id` `result.employee` ; champs additionnels tolérés pour rétrocompat API. */
export interface U3129EmployeeRef {
  id: string;
  codeEmployee?: string;
  firstName?: string;
  lastName?: string;
  /** GET détail : horaire vs salarié (non envoyé à la sauvegarde si le backend le gère côté RH). */
  remunerationType?: U3129RemunerationType;
  /** Base horaire (GET, lecture seule dans le formulaire) — peut être absent si seul `remunerationType` est fourni. */
  hourlyBase?: number | null;
  vacationBncHours?: number;
  vacationNegativeThresholdHours?: number;
  holidayBncHours?: number;
  sickLeaveBncHours?: number;
  specialBncHours?: number;
}

/**
 * Feuille de temps — corps `result` du GET `U3129/:id` (détail complet).
 * Les totaux `totalHours`, `totalPaidHours`, `totalExpenses`, `totalEquipment` peuvent provenir du search ou être absents sur le GET selon le backend.
 */
export interface U3129Timesheet {
  id: string;
  organizationId?: string;
  employeeId: string;
  periodDate: string;
  status: U3129TimesheetStatus;
  /** Total hebdomadaire retourné par le GET backend. */
  totalHours?: number;
  /** Total heures travaillées (honoraires / projets uniquement, sans banques d’heures) — recalcul UI. */
  totalPaidHours?: number;
  /** Total absences (vacances, fériés, maladie, autorisé) — calcul UI, non envoyé au backend. */
  totalLeaveHours?: number;
  /** Total retrait banque accumulée — UI. */
  totalSpecialWithdraw?: number;
  /** Total dépôt banque accumulée (positif en saisie) — UI. */
  totalSpecialDeposit?: number;
  /** Retrait − dépôt — UI. */
  totalSpecialNet?: number;
  /** Total monétaire des dépenses (search backend). */
  totalExpenses?: number;
  /** Total monétaire des équipements (search backend). */
  totalEquipment?: number;
  vacationBalanceBeforeTimesheet?: number;
  vacationDebitHours?: number;
  vacationBalanceAfterTimesheet?: number;
  vacationThresholdHours?: number;
  vacationThresholdExceededHours?: number;
  vacationBankWillBeNegative?: boolean;
  /** Nouveaux noms retournés par le GET backend. */
  vacationBncHours?: number;
  holidayBncHours?: number;
  sickLeaveBncHours?: number;
  specialBncHours?: number;
  unpaidHours?: number;
  regularHoursToBank?: number;
  halfTimeHoursToBank?: number;
  doubleTimeHoursToBank?: number;
  version: number;
  honoraria?: U3129HonorariumItem[];
  expenses?: U3129ExpenseItem[];
  equipment?: U3129EquipmentItem[];
  bankHoursList?: U3129BankHoursItem[];
  employee?: U3129EmployeeRef;
  /** Libellé affiché (prénom + nom), non envoyé au backend. */
  employeeDisplay?: string;
  /** Libellé affiché pour la période (lundi – dimanche), non envoyé au backend. */
  periodDisplay?: string;
  /** Libellé traduit du statut (UI, non envoyé au backend). */
  statusDisplay?: string;
  /**
   * Snapshot UI des heures payées issues du GET avant recalcul de `totalPaidHours` depuis les lignes.
   * Non envoyé au backend (voir `buildTimesheetUpdatePayload`).
   */
  approvalPaidHours?: number;
  /** Totaux footer onglet honoraires — heures projet uniquement (UI, non envoyé au backend). */
  honorariaFooterTotals?: Record<string, number>;
  /** Totaux footer — absences / banques (UI). */
  bankHoursFooterTotals?: Record<string, number>;
  /** Totaux combinés projet + absences (UI). */
  combinedWeekFooterTotals?: Record<string, number>;
}

/** Payload de recherche (employeeId obligatoire + option tous les employés). */
export interface U3129SearchPayload extends U2000SearchPayload {
  employeeId: string;
  showAllEmployees: boolean;
}

export const emptyTimesheet: U3129Timesheet = {
  id: "",
  employeeId: "",
  periodDate: "",
  status: "Open",
  totalHours: 0,
  totalPaidHours: 0,
  totalLeaveHours: 0,
  totalExpenses: 0,
  totalEquipment: 0,
  vacationBncHours: 0,
  holidayBncHours: 0,
  sickLeaveBncHours: 0,
  specialBncHours: 0,
  unpaidHours: 0,
  regularHoursToBank: 0,
  halfTimeHoursToBank: 0,
  doubleTimeHoursToBank: 0,
  approvalPaidHours: 0,
  version: 0,
  honoraria: [],
  expenses: [],
  equipment: [],
  bankHoursList: [],
};

/** Critères autocomplete (findProject / findSubproject). */
export type U3129AutoCompletCriterias = { fieldName: string; value: unknown }[];

/** Corps POST findAvailablePeriods : périodes candidates (lundis ISO) + employé cible. */
export interface U3129FindAvailablePeriodsRequest {
  employeeId: string;
  /** Liste des lundis ISO (YYYY-MM-DD) proposés côté UI ; le serveur renvoie le sous-ensemble autorisé. */
  periods: string[];
}

/** Corps POST findTasksForProject : tâches autorisées pour la combinaison projet / sous-projet. */
export interface U3129FindTasksForProjectRequest {
  projectId: string;
  /** Requis si le projet impose un sous-projet ; sinon omis ou vide selon règle backend. */
  subprojectId?: string | null;
}

/** Corps POST assignTasksToProjectScope : append assignations tâches actives au scope honoraires. */
export interface U3129AssignTasksToProjectScopeRequest {
  projectId: string;
  subprojectId?: string | null;
  taskIds: string[];
}

export interface U3129AssignTasksToProjectScopeResult {
  assignedCount: number;
}

/** Corps POST findCommonTasksForProjects — intersection tâches actives assignées à chaque projet (BL-0684). */
export interface U3129FindCommonTasksForProjectsRequest {
  projectIds: string[];
  employeeId?: string | null;
}

export interface U3129HonorariumTasksByProject {
  projectId: string;
  tasks: U3129TaskItem[];
}

export interface U3129FindCommonTasksForProjectsResponse {
  results: U3129TaskItem[];
  byProject: U3129HonorariumTasksByProject[];
}

/** Corps POST findActiveTasks — catalogue toutes tâches actives org (BL-0684). */
export interface U3129FindActiveTasksRequest {
  employeeId?: string | null;
}

/** Corps POST assignTasksToSelectedProjects (BL-0684). */
export interface U3129AssignTasksToSelectedProjectsRequest {
  projectIds: string[];
  taskIds: string[];
  employeeId?: string | null;
}

export interface U3129AssignTasksToSelectedProjectsByProject {
  projectId: string;
  assignedCount: number;
}

export interface U3129AssignTasksToSelectedProjectsResult {
  assignedCount: number;
  byProject: U3129AssignTasksToSelectedProjectsByProject[];
}

/** Corps POST `/U3129/initForCreate` — initialisation d’une feuille vide côté serveur (soldes, lignes par défaut, etc.). */
export interface U3129InitForCreateParams {
  employeeId: string;
  /** Lundi de la période (ISO). */
  periodDate: string;
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, payload: U3129SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, payload);
};

export const doFindProject = (axios: AxiosInstance, criterias: U3129AutoCompletCriterias) => {
  return axios.post(LogicalUnitEndpoints.FINDPROJECT, criterias);
};

export const doFindSubproject = (axios: AxiosInstance, criterias: U3129AutoCompletCriterias) => {
  return axios.post(LogicalUnitEndpoints.FINDSUBPROJECT, criterias);
};

export const doFindEmployee = (axios: AxiosInstance, criterias: U3129AutoCompletCriterias) => {
  return axios.post(LogicalUnitEndpoints.FINDEMPLOYEE, criterias);
};

export const doFindAvailablePeriods = (axios: AxiosInstance, body: U3129FindAvailablePeriodsRequest) => {
  return axios.post(LogicalUnitEndpoints.FINDAVAILABLEPERIODS, body);
};

export const doFindTasksForProject = (axios: AxiosInstance, body: U3129FindTasksForProjectRequest) => {
  return axios.post(LogicalUnitEndpoints.FINDTASKSFORPROJECT, body);
};

export const doFindTasksNotAssignedToProject = (axios: AxiosInstance, body: U3129FindTasksForProjectRequest) => {
  return axios.post(LogicalUnitEndpoints.FINDTASKSNOTASSIGNEDTOPROJECT, body);
};

export const doAssignTasksToProjectScope = (axios: AxiosInstance, body: U3129AssignTasksToProjectScopeRequest) => {
  return axios.post(LogicalUnitEndpoints.ASSIGNTASKSTOPROJECTSCOPE, body);
};

export const doFindCommonTasksForProjects = (axios: AxiosInstance, body: U3129FindCommonTasksForProjectsRequest) => {
  return axios.post(LogicalUnitEndpoints.FINDCOMMONTASKSFORPROJECTS, body);
};

export const doFindActiveTasks = (axios: AxiosInstance, body: U3129FindActiveTasksRequest = {}) => {
  return axios.post(LogicalUnitEndpoints.FINDACTIVETASKS, body);
};

export const doAssignTasksToSelectedProjects = (axios: AxiosInstance, body: U3129AssignTasksToSelectedProjectsRequest) => {
  return axios.post(LogicalUnitEndpoints.ASSIGNTASKSTOSELECTEDPROJECTS, body);
};

/**
 * Prépare les données par défaut pour une création de feuille de temps.
 * Envoie `employeeId` et `periodDate` dans le corps JSON (POST).
 * Réponse attendue : même enveloppe que les autres appels LU (`result` = corps timesheet partiel ou complet sans id persisté).
 */
export const doInitForCreate = (axios: AxiosInstance, params: U3129InitForCreateParams) => {
  const payload: U3129InitForCreateParams = {
    employeeId: params.employeeId.trim(),
    periodDate: params.periodDate.trim(),
  };
  return axios.post(LogicalUnitEndpoints.INIT_FOR_CREATE, payload);
};

export const doGet = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

function normalizeHourlyBaseFromApi(raw: unknown): number | null {
  if (raw === undefined || raw === null) return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

/** Premier candidat défini pour un nombre (y compris `0`) ; ignore `undefined`, `null`, `""`. */
function firstPresentNumericCandidate(...vals: unknown[]): unknown {
  for (const v of vals) {
    if (v === undefined || v === null || v === "") continue;
    return v;
  }
  return undefined;
}

/** Entier ou chaîne uniquement numérique — pour enums JSON (statut feuille). */
function firstPresentIntegerLikeCandidate(...vals: unknown[]): unknown {
  for (const v of vals) {
    if (v === undefined || v === null || v === "") continue;
    if (typeof v === "number" && Number.isInteger(v)) {
      return v;
    }
    if (typeof v === "string") {
      const t = v.trim();
      if (t !== "" && /^\d+$/.test(t)) {
        return parseInt(t, 10);
      }
    }
  }
  return undefined;
}

function pickHourlyBaseRawFromEmployee(emp: U3129EmployeeRef): unknown {
  const rec = emp as unknown as Record<string, unknown>;
  return firstPresentNumericCandidate(rec.hourlyBase, rec.hourly_base, rec.HourlyBase);
}

function pickHourlyBaseRawFromRowRoot(row: U3129Timesheet & { hourlyBase?: unknown }): unknown {
  const rec = row as unknown as Record<string, unknown>;
  return firstPresentNumericCandidate(row.hourlyBase, rec.hourly_base, rec.HourlyBase);
}

function firstPresentStringCandidate(...vals: unknown[]): string | undefined {
  for (const v of vals) {
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (s !== "") return s;
  }
  return undefined;
}

function pickRemunerationTypeFromEmployee(emp: U3129EmployeeRef): U3129RemunerationType | undefined {
  const rec = emp as unknown as Record<string, unknown>;
  const raw = firstPresentStringCandidate(rec.remunerationType, rec.remuneration_type, rec.RemunerationType);
  if (raw === "Hourly" || raw === "Salary") return raw;
  return emp.remunerationType;
}

const U3129_VALID_TIMESHEET_STATUSES = [
  "Open",
  "WaitingForApproval",
  "Approve",
  "WaitingForGenerateTEC",
  "Close",
] as const satisfies readonly U3129TimesheetStatus[];

function isU3129TimesheetStatusString(s: string): s is U3129TimesheetStatus {
  return (U3129_VALID_TIMESHEET_STATUSES as readonly string[]).includes(s);
}

/**
 * Statut sérialisé en entier (enum .NET en JSON). Ordre aligné sur TsTimesheetStatus côté backend.
 * Si l’ordre diverge, ajuster ce tableau uniquement.
 */
const U3129_TIMESHEET_STATUS_ENUM_INDEX: readonly U3129TimesheetStatus[] = [
  "Open",
  "WaitingForApproval",
  "Approve",
  "WaitingForGenerateTEC",
  "Close",
];

function u3129TimesheetStatusFromNumericEnum(v: unknown): U3129TimesheetStatus | undefined {
  if (v === undefined || v === null || v === "") return undefined;
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n) || !Number.isInteger(n)) return undefined;
  if (n < 0 || n >= U3129_TIMESHEET_STATUS_ENUM_INDEX.length) return undefined;
  return U3129_TIMESHEET_STATUS_ENUM_INDEX[n];
}

/** Réponses API PascalCase (`Employee`) ou camelCase (`employee`). */
function pickEmployeeObjectFromTimesheetRow(rec: Record<string, unknown>): U3129EmployeeRef | undefined {
  const emp = rec.employee ?? rec.Employee;
  if (emp != null && typeof emp === "object" && !Array.isArray(emp)) {
    return emp as U3129EmployeeRef;
  }
  return undefined;
}

/** Réponses API PascalCase (`Status`) ou camelCase (`status`) ; tolère enum numérique JSON. */
function pickStatusFromTimesheetRow(rec: Record<string, unknown>, fallback: U3129TimesheetStatus | undefined): U3129TimesheetStatus | undefined {
  const intLike = firstPresentIntegerLikeCandidate(rec.status, rec.Status, rec.timesheetStatus, rec.TimesheetStatus);
  const fromEnum = u3129TimesheetStatusFromNumericEnum(intLike);
  if (fromEnum) {
    return fromEnum;
  }
  const raw = firstPresentStringCandidate(rec.status, rec.Status, rec.timesheetStatus, rec.TimesheetStatus);
  if (raw && isU3129TimesheetStatusString(raw)) {
    return raw;
  }
  if (fallback != null && isU3129TimesheetStatusString(String(fallback))) {
    return fallback;
  }
  return undefined;
}

function pickEmployeeIdFromTimesheetRow(rec: Record<string, unknown>, fallback: string | undefined): string {
  const id = firstPresentStringCandidate(rec.employeeId, rec.EmployeeId) ?? fallback ?? "";
  return String(id).trim();
}

function omitKeys<T extends object, K extends keyof T>(obj: T, keys: readonly K[]): Omit<T, K> {
  const clone = { ...obj } as T;
  for (const key of keys) {
    delete clone[key];
  }
  return clone;
}

function normalizeExpenseLineFromApi(item: U3129ExpenseItem): U3129ExpenseItem {
  const sub = item.subproject;
  const subId = sub && typeof sub === "object" && "id" in sub ? String((sub as { id?: string }).id ?? "") : "";
  return {
    ...item,
    clientRowId: item.clientRowId?.trim() ? item.clientRowId : newHonorariumClientRowId(),
    projectId: item.project?.id ?? item.projectId ?? "",
    subprojectId: subId || item.subprojectId?.trim() || "",
  };
}

function normalizeEquipmentLineFromApi(item: U3129EquipmentItem): U3129EquipmentItem {
  const sub = item.subproject;
  const subId = sub && typeof sub === "object" && "id" in sub ? String((sub as { id?: string }).id ?? "") : "";
  return {
    ...item,
    clientRowId: item.clientRowId?.trim() ? item.clientRowId : newHonorariumClientRowId(),
    projectId: item.project?.id ?? item.projectId ?? "",
    subprojectId: subId || item.subprojectId?.trim() || "",
  };
}

/** Première valeur numérique finie ; sinon 0 — évite `null`/absent → champ décimal vide en lecture seule. */
function firstFiniteNumberOrZero(...vals: unknown[]): number {
  for (const v of vals) {
    if (v === null || v === undefined || v === "") continue;
    const n = typeof v === "number" ? v : Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

/** Première valeur numérique finie ; sinon `undefined` (ex. solde banque non affiché). */
function firstDefinedFiniteNumber(...vals: unknown[]): number | undefined {
  for (const v of vals) {
    if (v === null || v === undefined || v === "") continue;
    const n = typeof v === "number" ? v : Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

type U3129EmployeeBankBalanceKey = "vacationBncHours" | "holidayBncHours" | "sickLeaveBncHours" | "specialBncHours";

const U3129_EMP_BANK_SNAKE: Record<U3129EmployeeBankBalanceKey, string> = {
  vacationBncHours: "vacation_bnc_hours",
  holidayBncHours: "holiday_bnc_hours",
  sickLeaveBncHours: "sick_leave_bnc_hours",
  specialBncHours: "special_bnc_hours",
};

const U3129_EMP_VAC_NEG_THRESHOLD_KEYS = [
  "vacationNegativeThresholdHours",
  "vacation_negative_threshold_hours",
  "VacationNegativeThresholdHours",
] as const;

/** Clés JSON possibles côté employé (camelCase, snake_case, PascalCase). */
const U3129_EMP_BANK_PASCAL: Record<U3129EmployeeBankBalanceKey, string> = {
  vacationBncHours: "VacationBncHours",
  holidayBncHours: "HolidayBncHours",
  sickLeaveBncHours: "SickLeaveBncHours",
  specialBncHours: "SpecialBncHours",
};

/**
 * Soldes banque sur l’employé : d’abord uniquement depuis `employee` (toutes casses), puis repli racine timesheet.
 * Évite de recopier des zéros racine (`initForCreate`) quand l’employé porte les vrais soldes sous un autre nom de propriété.
 */
function mergeEmployeeBankBalancesFromTimesheetRow(emp: U3129EmployeeRef, rowRec: Record<string, unknown>): Partial<Pick<U3129EmployeeRef, U3129EmployeeBankBalanceKey>> {
  const empRec = emp as unknown as Record<string, unknown>;
  const out: Partial<Pick<U3129EmployeeRef, U3129EmployeeBankBalanceKey>> = {};
  (["vacationBncHours", "holidayBncHours", "sickLeaveBncHours", "specialBncHours"] as const).forEach((key) => {
    const sk = U3129_EMP_BANK_SNAKE[key];
    const pk = U3129_EMP_BANK_PASCAL[key];
    const fromEmployee = firstDefinedFiniteNumber(emp[key], empRec[sk], empRec[pk]);
    const v = fromEmployee !== undefined ? fromEmployee : firstDefinedFiniteNumber(rowRec[key], rowRec[sk]);
    if (v !== undefined) {
      (out as Record<string, number>)[key] = v;
    }
  });
  return out;
}

function resolveEmployeeVacationNegativeThreshold(emp: U3129EmployeeRef | undefined, rowRec: Record<string, unknown>): number | undefined {
  const empRec = (emp ?? {}) as Record<string, unknown>;
  return firstDefinedFiniteNumber(
    ...(U3129_EMP_VAC_NEG_THRESHOLD_KEYS.map((k) => empRec[k]) as unknown[]),
    ...(U3129_EMP_VAC_NEG_THRESHOLD_KEYS.map((k) => rowRec[k]) as unknown[]),
  );
}

/** Solde vacances affiché hors statut Ouvert — snapshot feuille puis repli snapshot employé / racine. */
function resolveClosedTimesheetVacationBncHours(
  data: U3129Timesheet,
  rowRec: Record<string, unknown>,
  mergedFromEmployee: Partial<Pick<U3129EmployeeRef, U3129EmployeeBankBalanceKey>>,
): number | undefined {
  const before = data.vacationBalanceBeforeTimesheet;
  const fromRow = firstDefinedFiniteNumber(rowRec.vacationBncHours, rowRec.vacation_bnc_hours);
  const fromEmployee = mergedFromEmployee.vacationBncHours;
  if (before !== undefined && Number.isFinite(before)) {
    if (Math.abs(before) >= 0.0001) {
      return before;
    }
    const alt = firstDefinedFiniteNumber(fromRow, fromEmployee);
    if (alt !== undefined && alt < -0.0001) {
      return alt;
    }
    const debit = data.vacationDebitHours;
    const after = data.vacationBalanceAfterTimesheet;
    if (
      (debit === undefined || Math.abs(debit) < 0.0001) &&
      after !== undefined &&
      Number.isFinite(after) &&
      after < -0.0001
    ) {
      return after;
    }
    return before;
  }
  return firstDefinedFiniteNumber(fromRow, fromEmployee, data.vacationBalanceAfterTimesheet);
}

/**
 * Feuille **Open** : soldes banque affichés dans l’onglet honoraires — priorité `employee`, puis racine du timesheet, puis employé initLU.
 * Tolère `initForCreate` / GET avec soldes sur `employee` et zéros sur la racine, ou l’inverse.
 */
export function u3129ResolveOpenBankBalanceDisplaySource(
  data: U3129Timesheet | null | undefined,
  contextEmployee: U3129InitLUEmployee | null,
): Pick<U3129EmployeeRef, U3129EmployeeBankBalanceKey> {
  if (data == null) {
    return {
      vacationBncHours: contextEmployee?.vacationBncHours,
      holidayBncHours: contextEmployee?.holidayBncHours,
      sickLeaveBncHours: contextEmployee?.sickLeaveBncHours,
      specialBncHours: contextEmployee?.specialBncHours,
    };
  }
  const emp = data.employee;
  const empRec = (emp ?? {}) as Record<string, unknown>;
  const rowRec = data as unknown as Record<string, unknown>;
  return {
    vacationBncHours: firstDefinedFiniteNumber(
      emp?.vacationBncHours,
      empRec.vacation_bnc_hours,
      empRec.VacationBncHours,
      contextEmployee?.vacationBncHours,
      data.vacationBncHours,
      rowRec.vacation_bnc_hours,
    ),
    holidayBncHours: firstDefinedFiniteNumber(
      emp?.holidayBncHours,
      empRec.holiday_bnc_hours,
      empRec.HolidayBncHours,
      contextEmployee?.holidayBncHours,
      data.holidayBncHours,
      rowRec.holiday_bnc_hours,
    ),
    sickLeaveBncHours: firstDefinedFiniteNumber(
      emp?.sickLeaveBncHours,
      empRec.sick_leave_bnc_hours,
      empRec.SickLeaveBncHours,
      contextEmployee?.sickLeaveBncHours,
      data.sickLeaveBncHours,
      rowRec.sick_leave_bnc_hours,
    ),
    specialBncHours: firstDefinedFiniteNumber(
      emp?.specialBncHours,
      empRec.special_bnc_hours,
      empRec.SpecialBncHours,
      contextEmployee?.specialBncHours,
      data.specialBncHours,
      rowRec.special_bnc_hours,
    ),
  };
}

/**
 * Soldes banque pour l’onglet honoraires (lignes BNC + solde projeté vacances), tous statuts.
 * Hors « Ouvert » : priorité aux soldes sous `employee`, puis snapshot racine — évite qu’une valeur racine
 * erronée (p. ex. confondue avec un plafond) n’écrase un solde employé à 0.
 */
export function u3129ResolveHonorariumBankBalanceDisplaySource(
  data: U3129Timesheet | null | undefined,
  contextEmployee: U3129InitLUEmployee | null,
): Pick<U3129EmployeeRef, U3129EmployeeBankBalanceKey> {
  if (data == null || data.status === "Open") {
    return u3129ResolveOpenBankBalanceDisplaySource(data, contextEmployee);
  }
  const emp = data.employee;
  const rowRec = data as unknown as Record<string, unknown>;
  if (emp) {
    const m = mergeEmployeeBankBalancesFromTimesheetRow(emp, rowRec);
    return {
      vacationBncHours: resolveClosedTimesheetVacationBncHours(data, rowRec, m),
      holidayBncHours: firstDefinedFiniteNumber(
        m.holidayBncHours,
        rowRec.holidayBncHours,
        rowRec.holiday_bnc_hours,
      ),
      sickLeaveBncHours: firstDefinedFiniteNumber(
        m.sickLeaveBncHours,
        rowRec.sickLeaveBncHours,
        rowRec.sick_leave_bnc_hours,
      ),
      specialBncHours: firstDefinedFiniteNumber(
        m.specialBncHours,
        rowRec.specialBncHours,
        rowRec.special_bnc_hours,
      ),
    };
  }
  return u3129ResolveOpenBankBalanceDisplaySource(data, contextEmployee);
}

/** Heures paye / banque : alias JSON possibles (camelCase, snake_case, PascalCase). */
function payrollHoursFromApiRow(row: Record<string, unknown>) {
  return {
    unpaidHours: firstFiniteNumberOrZero(row.unpaidHours, row.unpaid_hours, row.UnpaidHours),
    regularHoursToBank: firstFiniteNumberOrZero(row.regularHoursToBank, row.regular_hours_to_bank, row.RegularHoursToBank),
    halfTimeHoursToBank: firstFiniteNumberOrZero(row.halfTimeHoursToBank, row.half_time_hours_to_bank, row.HalfTimeHoursToBank),
    doubleTimeHoursToBank: firstFiniteNumberOrZero(row.doubleTimeHoursToBank, row.double_time_hours_to_bank, row.DoubleTimeHoursToBank),
    vacationBalanceBeforeTimesheet: firstDefinedFiniteNumber(
      row.vacationBalanceBeforeTimesheet,
      row.vacation_balance_before_timesheet,
      row.VacationBalanceBeforeTimesheet,
    ),
    vacationDebitHours: firstDefinedFiniteNumber(row.vacationDebitHours, row.vacation_debit_hours, row.VacationDebitHours),
    vacationBalanceAfterTimesheet: firstDefinedFiniteNumber(
      row.vacationBalanceAfterTimesheet,
      row.vacation_balance_after_timesheet,
      row.VacationBalanceAfterTimesheet,
    ),
    vacationThresholdHours: firstDefinedFiniteNumber(
      row.vacationThresholdHours,
      row.vacation_threshold_hours,
      row.VacationThresholdHours,
    ),
    vacationThresholdExceededHours: firstDefinedFiniteNumber(
      row.vacationThresholdExceededHours,
      row.vacation_threshold_exceeded_hours,
      row.VacationThresholdExceededHours,
    ),
    vacationBankWillBeNegative: (() => {
      const raw = row.vacationBankWillBeNegative ?? row.vacation_bank_will_be_negative ?? row.VacationBankWillBeNegative;
      if (raw === undefined || raw === null || raw === "") return undefined;
      if (typeof raw === "boolean") return raw;
      if (typeof raw === "number") return raw !== 0;
      if (typeof raw === "string") {
        const v = raw.trim().toLowerCase();
        if (v === "true" || v === "1") return true;
        if (v === "false" || v === "0") return false;
      }
      return undefined;
    })(),
  };
}

/**
 * Si `result` enveloppe le DTO feuille (`timesheet`, `data`, etc.), retourne la feuille interne.
 * Utile pour `initForCreate` dont la forme peut différer du GET / create.
 */
export function unwrapU3129LuTimesheetResultPayload(raw: unknown): U3129Timesheet | null {
  if (raw == null) {
    return null;
  }
  if (typeof raw !== "object" || Array.isArray(raw)) {
    return raw as U3129Timesheet;
  }
  const r = raw as Record<string, unknown>;
  const nested = r.timesheet ?? r.Timesheet ?? r.data ?? r.Data ?? r.payload ?? r.Payload;
  if (nested != null && typeof nested === "object" && !Array.isArray(nested)) {
    return nested as U3129Timesheet;
  }
  return raw as U3129Timesheet;
}

/** Normalise les honoraria reçues du GET (project/task) pour le formulaire (projectId/taskType). Complète toujours `bankHoursList`. */
export function normalizeTimesheetFromApi(row: U3129Timesheet): U3129Timesheet {
  const recTop = row as unknown as Record<string, unknown>;
  const statusAligned = pickStatusFromTimesheetRow(recTop, row.status);
  const employeeAligned = pickEmployeeObjectFromTimesheetRow(recTop);
  const employeeIdAligned = pickEmployeeIdFromTimesheetRow(recTop, row.employeeId);
  const rowAligned: U3129Timesheet = {
    ...row,
    ...(employeeIdAligned !== "" ? { employeeId: employeeIdAligned } : {}),
    ...(statusAligned !== undefined ? { status: statusAligned } : {}),
    ...(employeeAligned !== undefined ? { employee: employeeAligned } : {}),
  };

  const bankHoursList = ensureBankHoursList(rowAligned.bankHoursList);
  const rowLegacy = rowAligned as U3129Timesheet & { hourlyBase?: unknown };
  const rowWithoutRootHb = omitKeys(rowLegacy, ["hourlyBase"]);
  const emp = rowWithoutRootHb.employee;
  const hbEmployeeRaw = emp ? pickHourlyBaseRawFromEmployee(emp) : undefined;
  const rootHbRaw = pickHourlyBaseRawFromRowRoot(rowLegacy);
  const hourlyBaseMerged =
    hbEmployeeRaw !== undefined ? normalizeHourlyBaseFromApi(hbEmployeeRaw) : normalizeHourlyBaseFromApi(rootHbRaw);
  const rowRec = rowWithoutRootHb as unknown as Record<string, unknown>;
  const remunerationTypeMerged = emp ? pickRemunerationTypeFromEmployee(emp) : undefined;

  const shell: U3129EmployeeRef = { id: pickEmployeeIdFromTimesheetRow(rowRec, rowWithoutRootHb.employeeId) };
  const bankFromShellOnly = mergeEmployeeBankBalancesFromTimesheetRow(shell, rowRec);

  let employee: U3129EmployeeRef | undefined;
  if (emp) {
    const vacationNegativeThresholdHours = resolveEmployeeVacationNegativeThreshold(emp, rowRec);
    employee = {
      ...emp,
      hourlyBase: hourlyBaseMerged,
      ...(remunerationTypeMerged !== undefined ? { remunerationType: remunerationTypeMerged } : {}),
      ...mergeEmployeeBankBalancesFromTimesheetRow(emp, rowRec),
      ...(vacationNegativeThresholdHours !== undefined ? { vacationNegativeThresholdHours } : {}),
    };
  } else if (rootHbRaw !== undefined || hourlyBaseMerged !== null || Object.keys(bankFromShellOnly).length > 0) {
    const vacationNegativeThresholdHours = resolveEmployeeVacationNegativeThreshold(undefined, rowRec);
    employee = {
      ...shell,
      hourlyBase: hourlyBaseMerged,
      ...bankFromShellOnly,
      ...(vacationNegativeThresholdHours !== undefined ? { vacationNegativeThresholdHours } : {}),
    };
  } else {
    employee = undefined;
  }

  const base: U3129Timesheet = { ...rowWithoutRootHb, bankHoursList, employee };

  const honoraria = !base.honoraria?.length
    ? base.honoraria
    : base.honoraria.map((item) => {
        const sub = item.subproject;
        const subId = sub && typeof sub === "object" && "id" in sub ? String((sub as { id?: string }).id ?? "") : "";
        const projectId = item.project?.id ?? item.projectId ?? "";
        const uiRow = item as U3129HonorariumItem & { clientDisplay?: string; projectDisplay?: string };
        const clientDisplay =
          uiRow.clientDisplay?.trim() || u3129ClientDisplayFromHonorariumRow(item);
        return {
          ...item,
          clientRowId: item.clientRowId?.trim() ? item.clientRowId : newHonorariumClientRowId(),
          projectId,
          subprojectId: subId || item.subprojectId?.trim() || "",
          taskType: item.task?.id ?? item.taskType ?? "",
          projectDisplay: uiRow.projectDisplay?.trim() || u3129ProjectDisplayLabel(item.project, projectId),
          ...(clientDisplay ? { clientDisplay } : {}),
        };
      });

  const expenses = (base.expenses ?? []).map((item) => normalizeExpenseLineFromApi(item));
  const equipment = (base.equipment ?? []).map((item) => normalizeEquipmentLineFromApi(item));

  const payrollHours = payrollHoursFromApiRow(rowRec);
  const out = { ...base, ...payrollHours, honoraria, expenses, equipment };
  const idEmpty = !String(out.id ?? "").trim();
  return {
    ...out,
    ...(out.status === undefined && idEmpty ? { status: "Open" as U3129TimesheetStatus } : {}),
  } as U3129Timesheet;
}

export const doCreate = (axios: AxiosInstance, data: Partial<U3129Timesheet>) => {
  return axios.post(LogicalUnitEndpoints.CREATE, data);
};

/** Résout la métadonnée tâche pour une ligne honoraire (liste chargée par projet/sous-projet ou repli initLU). */
export type U3129ResolveHonorariumTask = (row: U3129HonorariumItem, rowIndex: number) => U3129TaskItem | undefined;

function stripExpenseLineForPayload(row: U3129ExpenseItem & { checked?: boolean }): Record<string, unknown> {
  return omitKeys(row, ["project", "subproject", "clientDisplay", "clientRowId", "checked"]);
}

function stripEquipmentLineForPayload(row: U3129EquipmentItem & { checked?: boolean }): Record<string, unknown> {
  return omitKeys(row, ["project", "subproject", "clientDisplay", "clientRowId", "checked"]);
}

function isExpenseLineCompleteForApi(row: Record<string, unknown>): boolean {
  const t = String(row.transactionDate ?? "").trim();
  const p = String(row.projectId ?? "").trim();
  const e = String(row.expenseTypeId ?? "").trim();
  const a = row.amount;
  const numOk = a !== undefined && a !== null && String(a).trim() !== "" && !Number.isNaN(Number(a));
  return !!(t && p && e && numOk);
}

function isEquipmentLineCompleteForApi(row: Record<string, unknown>): boolean {
  const t = String(row.transactionDate ?? "").trim();
  const p = String(row.projectId ?? "").trim();
  const eq = String(row.equipmentTypeId ?? "").trim();
  const bu = String(row.billingUnit ?? "").trim();
  const cost = row.equipmentCost;
  const amt = row.amount;
  const costOk = cost !== undefined && cost !== null && String(cost).trim() !== "" && !Number.isNaN(Number(cost));
  const amtOk = amt !== undefined && amt !== null && String(amt).trim() !== "" && !Number.isNaN(Number(amt));
  return !!(t && p && eq && bu && costOk && amtOk);
}

function omitEmptyOptionalKeys(row: Record<string, unknown>, keys: string[]): Record<string, unknown> {
  const out = { ...row };
  for (const k of keys) {
    const v = out[k];
    if (v === undefined || v === null || (typeof v === "string" && v.trim() === "")) {
      delete out[k];
    }
  }
  return out;
}

/** Jours lundi–dimanche sur les lignes honoraires / banques (API : nombre requis, pas `null`). */
const U3129_WEEK_DAY_HOUR_KEYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;

function normalizeWeekDayHourFieldsForApi(row: Record<string, unknown>): Record<string, unknown> {
  const out = { ...row };
  for (const k of U3129_WEEK_DAY_HOUR_KEYS) {
    const v = out[k];
    if (v === null || v === undefined || v === "") {
      out[k] = 0;
      continue;
    }
    const n = typeof v === "number" ? v : Number(v);
    out[k] = Number.isFinite(n) ? n : 0;
  }
  return out;
}

export function buildExpensesPayloadForApi(rows: U3129ExpenseItem[] | undefined | null): Array<Record<string, unknown>> {
  const list = rows ?? [];
  return list
    .map((item) => stripExpenseLineForPayload(item as U3129ExpenseItem & { checked?: boolean }))
    .filter(isExpenseLineCompleteForApi)
    .map((row) => omitEmptyOptionalKeys(row, ["subprojectId", "taxRateId", "remark", "quantity", "unitCost"]));
}

export function buildEquipmentPayloadForApi(rows: U3129EquipmentItem[] | undefined | null): Array<Record<string, unknown>> {
  const list = rows ?? [];
  return list
    .map((item) => stripEquipmentLineForPayload(item as U3129EquipmentItem & { checked?: boolean }))
    .filter(isEquipmentLineCompleteForApi)
    .map((row) => omitEmptyOptionalKeys(row, ["subprojectId", "remark", "quantity"]));
}

export type U3129TimesheetSavePayload = Omit<Partial<U3129Timesheet>, "honoraria" | "expenses" | "equipment"> & {
  honoraria?: Array<Record<string, unknown>>;
  expenses?: Array<Record<string, unknown>>;
  equipment?: Array<Record<string, unknown>>;
};

/** Prépare le payload de sauvegarde (create/update) :
 *  - retire les champs calculés côté backend (`organizationId`, `vacationBncHours`, `holidayBncHours`, `sickLeaveBncHours`, `specialBncHours`, `unpaidHours`, `regularHoursToBank`, `halfTimeHoursToBank`, `doubleTimeHoursToBank`, `totalHours`) ; réinjecte `totalPaidHours` comme somme travaillé + congés (aligné lignes).
 *  - retire les champs purement UI (libellés, totaux footer, totalHours sur les lignes banque, employee.hourlyBase lecture seule GET)
 *  - pour chaque ligne honoraire, inclut task au format { id, description } (description = taskCode de la tâche) et retire taskType, clientRowId, checked.
 *  - envoie `bankHoursList` normalisée (types {@link U3129_BANK_HOUR_TYPE_ORDER}) sans totalHours.
 *  - envoie `expenses` et `equipment` comme listes nettoyées (sans objets projet/sous-projet ni champs UI) ; lignes incomplètes exclues.
 */
export function buildTimesheetUpdatePayload(data: Partial<U3129Timesheet>, resolveTask: U3129ResolveHonorariumTask): U3129TimesheetSavePayload {
  const honoraria = data.honoraria;
  const expensesRaw = data.expenses;
  const equipmentRaw = data.equipment;
  const _bankListRaw = data.bankHoursList;
  const restData = omitKeys(data, [
    "organizationId",
    "totalHours",
    "vacationBncHours",
    "holidayBncHours",
    "sickLeaveBncHours",
    "specialBncHours",
    "unpaidHours",
    "regularHoursToBank",
    "halfTimeHoursToBank",
    "doubleTimeHoursToBank",
    "approvalPaidHours",
    "totalLeaveHours",
    "totalSpecialWithdraw",
    "totalSpecialDeposit",
    "totalSpecialNet",
    "employeeDisplay",
    "periodDisplay",
    "honorariaFooterTotals",
    "bankHoursFooterTotals",
    "combinedWeekFooterTotals",
    "honoraria",
    "bankHoursList",
    "expenses",
    "equipment",
  ]);

  const bankHoursList = ensureBankHoursList(_bankListRaw).map((row) => {
    const withoutTotal = omitKeys(row as U3129BankHoursItem & { totalHours?: number }, ["totalHours"]);
    return normalizeWeekDayHourFieldsForApi(withoutTotal as Record<string, unknown>) as unknown as U3129BankHoursItem;
  });

  const projectH = typeof data.totalPaidHours === "number" && Number.isFinite(data.totalPaidHours) ? data.totalPaidHours : 0;
  const absenceH = typeof data.totalLeaveHours === "number" && Number.isFinite(data.totalLeaveHours) ? data.totalLeaveHours : 0;
  const specialNetH = typeof data.totalSpecialNet === "number" && Number.isFinite(data.totalSpecialNet) ? data.totalSpecialNet : 0;

  const payload: U3129TimesheetSavePayload = {
    ...restData,
    bankHoursList,
    expenses: buildExpensesPayloadForApi(expensesRaw),
    equipment: buildEquipmentPayloadForApi(equipmentRaw),
    /** Aligné backend `u3129ComputeTotalPaidHours` : projet + absences + (retrait − dépôt). */
    totalPaidHours: Math.round((projectH + absenceH + specialNetH) * 100) / 100,
  };

  if (payload.employee) {
    const empRest = omitKeys(payload.employee as U3129EmployeeRef & { hourlyBase?: number | null }, ["hourlyBase"]);
    payload.employee = empRest as U3129EmployeeRef;
  }

  if (!honoraria?.length) {
    return { ...payload, honoraria: [] };
  }

  const honorariaPayload = honoraria.map((row, rowIndex) => {
    const taskId = row.taskType;
    const taskMeta = resolveTask(row, rowIndex);
    const taskPayload = taskId
      ? {
          id: taskId,
          description: taskMeta?.taskCode ?? row.task?.description ?? "",
        }
      : undefined;
    const rest = omitKeys(row as U3129HonorariumItem & { checked?: boolean }, [
      "taskType",
      "clientRowId",
      "checked",
      "clientDisplay",
      "projectDisplay",
    ]);
    const withTask = taskPayload ? { ...rest, task: taskPayload } : rest;
    const projectObj = (withTask as Record<string, unknown>).project;
    if (projectObj && typeof projectObj === "object") {
      (withTask as Record<string, unknown>).project = omitKeys(projectObj as Record<string, unknown>, ["workNatureDisplay"]);
    }
    const taskObj = (withTask as Record<string, unknown>).task;
    if (taskObj && typeof taskObj === "object") {
      (withTask as Record<string, unknown>).task = omitKeys(taskObj as Record<string, unknown>, [
        "workNatureDisplay",
        "workNatureDescriptionFr",
        "workNatureDescriptionEn",
      ]);
    }
    const cleaned = omitEmptyOptionalKeys(withTask, ["subprojectId"]);
    return normalizeWeekDayHourFieldsForApi(cleaned as Record<string, unknown>);
  });
  return { ...payload, honoraria: honorariaPayload };
}

/** Retourne une promesse dont response.data.result contient l'entité (compatible hook useU2000SaveHandlers). */
export const doUpdate = (axios: AxiosInstance, id: string | number, data: Partial<U3129Timesheet>) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), data).then((res) => ({
    ...res,
    data: { result: res.data?.result ?? res.data },
  }));
};

export const doDelete = (axios: AxiosInstance, id: string | number, data?: Record<string, unknown>) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), data ?? {});
};

/** Soumission pour approbation (Open → WaitingForApproval). Corps : id + version (verrou optimiste). */
export const doSubmitForApproval = (axios: AxiosInstance, id: string | number, body: { id: string; version: number }) => {
  return axios.post(LogicalUnitEndpoints.SUBMIT_FOR_APPROVAL(id), body).then((res) => ({
    ...res,
    data: { result: res.data?.result ?? res.data },
  }));
};

/** Réouverture (WaitingForApproval → Open). Corps : id + version (verrou optimiste). */
export const doReopen = (axios: AxiosInstance, id: string | number, body: { id: string; version: number }) => {
  return axios.post(LogicalUnitEndpoints.REOPEN(id), body).then((res) => ({
    ...res,
    data: { result: res.data?.result ?? res.data },
  }));
};

export const doGetAudits = (axios: AxiosInstance, id: string | number) => {
  return axios.get(LogicalUnitEndpoints.AUDITS(id), {
    params: { b: U2000GenerateUrlUUID() },
  });
};

export function luHandleApiError(t: TranslateFn, NS: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; errorType?: string; description?: string };

  if (apiErr?.errorCode === "B0014" || apiErr?.errorType === "BankBalanceExceeded") {
    errorResult.genericErrors.push({
      title: t("errorTitle", { ns: NS }),
      text: t("msgBankHoursExceedsBalance", {
        ns: NS,
        defaultValue: "Le solde d'une des banques d'heures a été dépassé.",
      }),
    });
  }
}
